import { p as proxyCustomElement, H, c as createEvent, h, a as Host } from './index.js';
import { s as slotFilled } from './p-rUtJRH95.js';
import { d as defineCustomElement$6, T as TablePageLength } from './p-DjHd24Sz.js';
import { d as defineCustomElement$o } from './p-BbTg3Car.js';
import { d as defineCustomElement$n } from './p-D5W-FK48.js';
import { d as defineCustomElement$m } from './p-BwWHobpA.js';
import { d as defineCustomElement$l } from './p-CgjIzafk.js';
import { d as defineCustomElement$k } from './p-RWl7wfjL.js';
import { d as defineCustomElement$j } from './p-BDIehgMZ.js';
import { d as defineCustomElement$i } from './p-DKss2Fy6.js';
import { d as defineCustomElement$h } from './p-DMGlYwB7.js';
import { d as defineCustomElement$g } from './p-DvXL3Xv_.js';
import { d as defineCustomElement$f } from './p-CEikBRMP.js';
import { d as defineCustomElement$e } from './p-Bi4IrIfv.js';
import { d as defineCustomElement$d } from './p-lnuA9A6c.js';
import { d as defineCustomElement$c } from './p-2MYQ9zL1.js';
import { d as defineCustomElement$b } from './p-D0-FzDC-.js';
import { d as defineCustomElement$a } from './p-Cw1JETmh.js';
import { d as defineCustomElement$9 } from './p-C-lPGEWX.js';
import { d as defineCustomElement$8 } from './p-CW-Ftoto.js';
import { d as defineCustomElement$7 } from './p-iPeSdhcz.js';
import { d as defineCustomElement$5 } from './p-JHo8RExW.js';
import { d as defineCustomElement$4 } from './p-rpBx4xIP.js';
import { d as defineCustomElement$3 } from './p-Trmgzgcp.js';
import { d as defineCustomElement$2 } from './p-KbTC_jnR.js';

const createStateDebouncer = (delayTime, holdTime, callback) => {
    let timeoutA;
    let timeoutB;
    let timeStarted;
    const _nullify = (timeout) => {
        if (timeout !== null) {
            clearTimeout(timeout);
        }
        return null;
    };
    let prevPersistedState;
    return (state, _callback = callback) => {
        // When going into state
        if (state) {
            // Clear all previous timeouts because we're restarting the cycle
            timeoutA = _nullify(timeoutA);
            timeoutB = _nullify(timeoutB);
            // Wait for x ms before state change (debounce)
            timeoutA = setTimeout(() => {
                // Set a time marker to indicate when we went into this state
                timeStarted = Date.now();
                if (prevPersistedState !== state) {
                    prevPersistedState = state;
                    // Fire callback to go into the new state
                    _callback?.(true);
                }
                timeoutA = _nullify(timeoutA);
            }, delayTime);
        }
        else {
            if (timeoutA !== null) {
                // If still waiting on first timeout, cancel the whole thing because that means we will have never even
                // fired the first callback to go into the state change
                timeoutA = _nullify(timeoutA);
                // Fire callback only if it's a specific inner callback
                if (_callback && _callback !== callback) {
                    // Reset prev persisted state
                    prevPersistedState = undefined;
                    _callback(false);
                }
                return;
            }
            // Check how long we've already been in this state
            const timeHeld = timeStarted ? Date.now() - timeStarted : holdTime;
            // Calc how long we still have to hold this state
            const timeLeft = holdTime - timeHeld;
            // Set timeout for the remaining time
            timeoutB = setTimeout(() => {
                // Reset time marker
                timeStarted = null;
                // Aaaaaaaand we're done (for now)
                if (prevPersistedState !== state) {
                    prevPersistedState = state;
                    _callback?.(false);
                }
                timeoutB = _nullify(timeoutB);
            }, timeLeft < 0 ? 0 : timeLeft);
        }
    };
};

const objectsEqual = (a, b, keys) => {
    if (a === b) {
        // Strict equal always wins
        return true;
    }
    // Check types (fast check)
    if (typeof a !== typeof b) {
        return false;
    }
    if (typeof a !== 'undefined' && typeof b !== 'undefined') {
        const keysOfA = a && typeof a === 'object' ? Object.keys(a) : [];
        const keysOfB = b && typeof b === 'object' ? Object.keys(b) : [];
        // Check total length of keys (fast check)
        if (keysOfA.length !== keysOfB.length) {
            return false;
        }
        const compareKeys = keys || keysOfB;
        // Find first value that fails equality check, if there is one, the objects are NOT equal
        return !compareKeys.find((key) => a?.[key] !== b?.[key]);
    }
    return false;
};
/**
 * Simple redux-like Store class. Uses a reducer function to compute a new state.
 * State can only be reduced by dispatching actions.
 *
 * When state changes, all subscribers (that match equality check) will be fired.
 */
class SimpleStore {
    constructor(reducer) {
        this.subscribers = [];
        this.reducer = reducer;
        // Fire reducer for the first time. This should return the initial state.
        this.state = this.reduce(this.state);
    }
    getState() {
        return this.state;
    }
    getPrevState() {
        return this.prevState;
    }
    dispatch(action) {
        this.prevState = this.state;
        this.state = this.reduce(this.state, action);
        // console.debug({ action, prevState: this.prevState, state: this.state });
        // If reducer didn't return same object
        if (this.prevState !== this.state) {
            this.notifySubscribers();
        }
        return action;
    }
    /**
     * Subscribe to state changes.
     *
     * @param fn Handler function that receives previous and new state
     * @param preventEmitIfEqual Prevent calling handler based on shallow state equality check. Pass array of keys to filter which keys get checked for equality.
     */
    subscribe(fn, preventEmitIfEqual = true) {
        this.subscribers.push([fn, preventEmitIfEqual]);
        // Return cleanup function that filters itself from list of subscribers
        return () => {
            this.subscribers = this.subscribers.filter(([f]) => f === fn);
        };
    }
    /**
     * Get a promise that resolves when the given comparison function returns true.
     *
     * Useful when you want to wait for a single state change.
     *
     * @param checker Function that receives previous and new state. Return true to resolve promise.
     */
    waitFor(checker) {
        return new Promise((resolve) => {
            const unsub = this.subscribe((prevState, state) => {
                if (checker(prevState, state)) {
                    resolve(state);
                    unsub();
                }
            }, false);
        });
    }
    //
    // Private methods
    // ----------------------------
    reduce(state, action) {
        return this.reducer(state, action);
    }
    notifySubscribers() {
        for (const [subscriber, check] of this.subscribers) {
            let fireSubscriber = true;
            // Quick equality check
            if (check) {
                // Find first value that is not equal. If it doesn't return anything, the objects are equal.
                fireSubscriber = !objectsEqual(this.prevState, this.state, Array.isArray(check) ? check : undefined);
            }
            if (!fireSubscriber) {
                return;
            }
            // console.debug({ subscriber, prevState: this.prevState, state: this.state });
            // Fire subscriber
            subscriber(this.prevState, this.state);
        }
    }
}

const INITIAL_STATE = {
    page: 1,
    pageLength: 10,
    selectedKeys: [],
    isLoading: false,
    total: 0,
    totalUnfiltered: null,
};

//#region src/module/sort/index.ts
function get(obj, accessor) {
	if (typeof obj === "undefined") return void 0;
	if (typeof accessor === "undefined") return obj;
	if (typeof accessor === "function") return accessor(obj);
	return obj[accessor];
}
function spaceship(a, b) {
	if (a < b) return -1;
	if (a > b) return 1;
	return 0;
}
function toNumber(a) {
	if (typeof a === "string") return +a;
	if (a instanceof Date) return +a;
	if (typeof a === "number") return a;
	return +String(a);
}
function getSortFnNumeric(accessor) {
	return (a, b) => {
		return toNumber(get(a, accessor)) - toNumber(get(b, accessor));
	};
}
function getSortFnAlphaNumeric(accessor) {
	return (a, b) => {
		const _a = String(get(a, accessor) ?? "");
		const _b = String(get(b, accessor) ?? "");
		return Intl.Collator().compare(_a.toLowerCase(), _b.toLowerCase());
	};
}
function getSortFnDate(accessor) {
	return (a, b) => {
		return spaceship(toNumber(get(a, accessor) ?? 0), toNumber(get(b, accessor) ?? 0));
	};
}
function getSortFn(sortType, accessor) {
	switch (sortType) {
		case "string": return getSortFnAlphaNumeric(accessor);
		case "date": return getSortFnDate(accessor);
		default: return getSortFnNumeric(accessor);
	}
}
function guessSortFn(data, accessor) {
	if (!data.length) return;
	const firstItem = data.slice().shift();
	const value = get(firstItem, accessor);
	if (value instanceof Date) return getSortFnDate(accessor);
	if (typeof value === "string") return getSortFnAlphaNumeric(accessor);
	return getSortFnNumeric(accessor);
}

function _slicePage(page, pageLength, arr) {
    let start = pageLength * page - pageLength;
    if (start < 0) {
        start = 0;
    }
    let end = pageLength * page;
    if (end > arr.length) {
        end = arr.length;
    }
    return arr.slice(start, end);
}
function _sliceOffset(limit, offset, arr) {
    return arr.slice(offset, offset + limit);
}
/**
 * Simple class that you can fill with data, and it will handle the sorting, filtering and pagination for you as
 * if it were a server side API.
 *
 * @export
 * @class DataProvider
 */
class DataProvider {
    constructor(input, keyField) {
        this.setData(input, keyField);
    }
    /**
     * Request data with parameters. Will apply sorting, filtering and pagination.
     */
    async request(params) {
        const { sortType, sortField, sortOrder, dataFilter } = params;
        const allData = await this._dataPromise;
        if (typeof allData === 'undefined') {
            return {
                total: 0,
                totalUnfiltered: null,
                data: undefined,
            };
        }
        // 1. Filter the complete set
        const filteredData = this._filterData(allData, dataFilter);
        // 2. Sort that filtered subset
        let sortFn;
        if (sortField) {
            sortFn = sortType
                ? getSortFn(sortType, sortField.toString())
                : guessSortFn(filteredData, sortField.toString());
        }
        const sortedData = this._sortData(filteredData, sortFn, sortOrder);
        // 3. Slice the filtered and sorted subset and set as rows to render
        let slicedData;
        if (typeof params.page === 'number' && typeof params.pageLength === 'number') {
            slicedData = this._sliceDataPaginated(sortedData, params.page, params.pageLength);
        }
        else {
            slicedData = sortedData;
        }
        return {
            total: filteredData.length,
            // We'll return undefined if no filter was applied
            totalUnfiltered: dataFilter ? allData.length : null,
            data: slicedData,
        };
    }
    async getDataPromise() {
        return this._dataPromise;
    }
    getData() {
        return this._data;
    }
    /**
     * Set the source data through a plain array or with a Promise that returns a plain array.
     */
    setData(input, keyField) {
        this._keyField = keyField;
        if (Array.isArray(input)) {
            // Cache our data pool
            this._data = input.slice();
            this._dataPromise = Promise.resolve(this._data);
        }
        else {
            // Wait for input promise to resolve
            this._dataPromise = input.then((data) => {
                // Cache our data pool
                this._data = data.slice();
                return this._data;
            });
        }
        return this._dataPromise;
    }
    get(id) {
        if (typeof this._keyField === 'undefined') {
            throw new Error('DataProvider does not have unique `keyField`!');
        }
        if (this._data && this._keyField) {
            return this._data.find((d) => d[this._keyField] === id);
        }
    }
    getIndex(idx) {
        return this._data?.[idx];
    }
    add(data) {
        if (typeof this._data === 'undefined') {
            this._data = [];
        }
        if (Array.isArray(data)) {
            this._data.push(...data);
        }
        else {
            this._data.push(data);
        }
    }
    remove(removeIds) {
        if (typeof this._keyField === 'undefined') {
            throw new Error('DataProvider does not have unique `keyField`!');
        }
        if (!this._data) {
            return [];
        }
        const removed = [];
        this._updateData(this._data.filter((d) => {
            const id = d[this._keyField];
            const remove = removeIds.includes(id);
            if (remove) {
                removed.push(id);
            }
            return !remove;
        }));
        return removed;
    }
    removeIndices(removeIndices) {
        if (!this._data) {
            return [];
        }
        const removed = [];
        this._updateData(this._data.filter((_, idx) => {
            const remove = removeIndices.includes(idx);
            if (remove) {
                removed.push(idx);
            }
            return !remove;
        }));
        return removed;
    }
    update(id, newDatum) {
        if (typeof this._keyField === 'undefined') {
            throw new Error('DataProvider does not have unique `keyField`!');
        }
        const idx = this._data ? this._data.findIndex((d) => d[this._keyField] === id) : -1;
        if (idx !== -1) {
            this.updateIndex(idx, newDatum);
        }
    }
    updateIndex(index, newDatum) {
        if (this._data) {
            this._data[index] = newDatum;
        }
    }
    copyData(fn) {
        if (!this._dataCopy) {
            // Create a copy of the current data
            this._dataCopy = this._data ? [...this._data] : undefined;
        }
        this._updateData(fn(this._dataCopy ? [...this._dataCopy] : undefined));
    }
    restoreData() {
        if (this._dataCopy) {
            this._updateData([...this._dataCopy]);
            this._dataCopy = undefined;
        }
    }
    _updateData(data) {
        this._data = data;
        this._dataPromise = Promise.resolve(this._data);
    }
    _sliceDataPaginated(data, page, pageLength) {
        return _slicePage(page, pageLength, data);
    }
    _sliceDataOffset(data, limit, offset) {
        return _sliceOffset(limit, offset, data);
    }
    _filterData(data, filterFn) {
        if (typeof filterFn !== 'function') {
            return data;
        }
        // Try to filter a copy of the data
        const filteredData = filterFn(data.slice());
        if (Array.isArray(filteredData)) {
            return filteredData;
        }
        // Somehow the filter didn't return an array, so on failure return the normal data
        return data;
    }
    _sortData(data, sortFn, sortOrder = 'asc') {
        let ret = data;
        if (typeof sortFn === 'function') {
            // Always sort a copy by using slice()
            ret = ret.slice().sort(sortFn);
            if (sortOrder === 'dsc') {
                ret = ret.reverse();
            }
        }
        return ret;
    }
}

const findKey = (obj, keys) => keys.find((key) => key in obj);
function dataToRecord(datum, mapping, fallbackKey) {
    // Default to false
    // We could add a mapping for this sometime if there is a situation where the server manages selection.
    const isSelected = false;
    if (typeof mapping === 'function') {
        const { selectable, key } = mapping(datum);
        if (typeof key !== 'string') {
            console.warn('Table datum key must be of type string!');
        }
        return {
            key,
            selectable,
            isSelected,
            data: datum,
        };
    }
    const { selectableField, keyField } = mapping;
    // If we don't have a mapping to key, attempt to find 'id' or 'key' in the data
    const foundKeyField = keyField || findKey(datum, ['id', 'key']);
    // If there's no unique key in the datum, use a key that was passed manually
    const key = foundKeyField ? datum[foundKeyField] : fallbackKey;
    if (typeof key !== 'string') {
        console.warn('Table datum key must be of type string!');
    }
    // If we don't have a mapping to selectable, attempt to find 'selectable' in the data
    const foundSelectableField = selectableField || findKey(datum, ['selectable']);
    // Selectable defaults to undefined so that the table `selectable` takes precedence
    const selectable = foundSelectableField ? Boolean(datum[foundSelectableField]) : undefined;
    return {
        key,
        selectable,
        isSelected,
        data: datum,
    };
}
function dataToRecords(data, mapping, offset = 0) {
    if (typeof data === 'undefined') {
        // Return empty array
        return [];
    }
    return data.map((datum, k) => {
        // When mapping an array, we use its index (+1 to not be zero based) and a possible offset as the unique key
        const key = String(k + 1 + offset);
        return dataToRecord(datum, mapping, key);
    });
}
function calculateOffset(page, pageLength = 0) {
    return page * pageLength - pageLength;
}
function createRequestFunctionWithDataProvider(input) {
    return (params) => {
        // Create data provider with given input
        const dataProvider = new DataProvider(input);
        const { page, pageLength, sortField, sortOrder, dataFilter, sortType } = params;
        const filterFunction = typeof dataFilter === 'function' ? dataFilter : undefined;
        return dataProvider.request({
            page,
            pageLength,
            sortField,
            sortOrder,
            sortType,
            dataFilter: filterFunction,
        });
    };
}
function getRetryFunction(opts) {
    if (typeof opts === 'undefined') {
        return;
    }
    switch (opts.type) {
        case 'previousPage':
            return (params, result, count) => {
                const limit = typeof opts.limit === 'undefined' ? 1 : opts.limit;
                if (limit && count > limit) {
                    // Stop retrying when limit is reached
                    return;
                }
                if (params.page > 1 && (!result.data || result.data.length === 0)) {
                    // Go back one page
                    return { ...params, page: params.page - 1 };
                }
            };
        case 'firstPage':
            return (params, result) => {
                if (params.page > 1 && (!result.data || result.data.length === 0)) {
                    // Go back to page 1
                    return { ...params, page: 1 };
                }
            };
    }
}
const uniqArray = (arr) => arr.filter((elem, pos, r) => r.indexOf(elem) === pos);
const arraysEqual = (a, b) => {
    // If they're both the same object, they're definitely equal
    if (a === b) {
        return true;
    }
    // If lengths don't match, we can always assume the arrays are not equal
    if (a.length !== b.length) {
        return false;
    }
    // Find a value in `a` that is NOT in `b`
    const unmatchedItem = a.find((v) => b.indexOf(v) === -1);
    if (unmatchedItem) {
        // There's a value in `b` that we don't have in `a`, so the arrays are not equal
        return false;
    }
    return true;
};

/**
 * Ensure that records get their `isSelected` property set when its key occurs in `selectedKeys`
 */
function mapSelectedRecords(records, selectedKeys = []) {
    if (!Array.isArray(records)) {
        // Return as-is
        return records;
    }
    return records.map((record) => {
        const shouldBeSelected = selectedKeys.indexOf(record.key) !== -1;
        // Only update the `isSelected` value when it is allowed!
        //
        // Note: the `selectable` property will be `undefined` when we don't
        // care about whether or not rows are selectable.
        //
        // This is why we explicitly want to check if it is not allowed (`false`).
        // If the record's key is in selected keys, mark it as selected
        record.isSelected = shouldBeSelected ? record.selectable !== false : false;
        return record;
    });
}
function mapSelectedKeys(selectedKeys, records) {
    if (!selectedKeys.length) {
        // Return same array so we don't trigger a state change
        return selectedKeys;
    }
    if (!Array.isArray(records)) {
        // Empty selected keys when records are empty
        return [];
    }
    // Only keep the selected keys that we currently have in the records
    return selectedKeys.filter((key) => {
        return records.find((r) => r.key === key);
    });
}
function tableDataSourceReducer(state, action) {
    if (typeof state === 'undefined') {
        return INITIAL_STATE;
    }
    if (action) {
        switch (action.type) {
            case 'UPDATE': {
                // Side effects:
                // - set `isLoading` to false
                // - set record `isSelected` based on the current `selectedKeys`
                const selectedKeys = mapSelectedKeys(state.selectedKeys, action.data.records);
                const records = mapSelectedRecords(action.data.records, selectedKeys);
                let forceSortOrder = action.data.sortOrder;
                if (action.data.sortField && !action.data.sortOrder) {
                    forceSortOrder = DEFAULT_SORT_ORDER;
                }
                return {
                    ...state,
                    // Spread all data without modification
                    ...action.data,
                    sortOrder: forceSortOrder,
                    // We're no longer loading when we have data
                    isLoading: false,
                    hasError: undefined,
                    records,
                    selectedKeys,
                };
            }
            case 'UPDATE_PARAMS': {
                let forceSortOrder = action.data.sortOrder;
                if (action.data.sortField && !action.data.sortOrder) {
                    forceSortOrder = DEFAULT_SORT_ORDER;
                }
                return {
                    ...state,
                    // Spread all data without modification
                    ...action.data,
                    sortOrder: forceSortOrder,
                };
            }
            case 'UPDATE_RECORD': {
                if (state.records && state.records[action.index] === action.record) {
                    // Return the same state if nothing is going to change
                    return state;
                }
                // Copy current records instead of modifying original data
                const records = [...(state.records || [])];
                // Mark as selected if in our selected keys
                action.record.isSelected = state.selectedKeys.indexOf(action.record.key) !== -1;
                // Replace record at index
                records[action.index] = action.record;
                return {
                    ...state,
                    records,
                };
            }
            case 'SET_LOADING':
                if (state.isLoading === action.isLoading) {
                    // Return the same state if nothing is going to change
                    return state;
                }
                return {
                    ...state,
                    isLoading: action.isLoading,
                };
            case 'SELECT_KEYS': {
                // Side-effect: update records `isSelected` property
                let keysAfterSelect;
                if (action.clearAll) {
                    // Discard any keys that are currently selected and replace them with the new ones.
                    keysAfterSelect = [...action.keys];
                }
                else {
                    // The new selected keys are the ones already selected combined with the new ones.
                    // Ensure there are no duplicates though!
                    keysAfterSelect = uniqArray([...state.selectedKeys, ...action.keys]);
                }
                // Perform a check to see if keys are equal regardless of order
                if (arraysEqual(state.selectedKeys, keysAfterSelect)) {
                    // Keep the current state
                    return state;
                }
                const records = mapSelectedRecords(state.records, keysAfterSelect);
                return {
                    ...state,
                    selectedKeys: keysAfterSelect,
                    records,
                };
            }
            case 'DESELECT_KEYS': {
                // Side-effect: update records `isSelected` property
                const keysAfterDeselect = state.selectedKeys.filter((v) => action.keys.indexOf(v) === -1);
                // Perform a check to see if keys are equal regardless of order
                if (arraysEqual(state.selectedKeys, keysAfterDeselect)) {
                    // Keep the current state
                    return state;
                }
                const records = mapSelectedRecords(state.records, keysAfterDeselect);
                return {
                    ...state,
                    // Keep all keys that are not found in keys to deselect
                    selectedKeys: keysAfterDeselect,
                    records,
                };
            }
            case 'SELECT_ALL_KEYS': {
                // Side-effect: update records `isSelected` property
                // Get all selectable records and their key
                let keysAfterSelectAll = (state.records || [])
                    .filter((record) => record.selectable !== false)
                    .map((record) => record.key);
                if (!action.clearAll) {
                    // Add to existing selection
                    keysAfterSelectAll = uniqArray([...state.selectedKeys, ...keysAfterSelectAll]);
                }
                // Perform a check to see if keys are equal regardless of order
                if (arraysEqual(state.selectedKeys, keysAfterSelectAll)) {
                    // Keep the current state
                    return state;
                }
                const records = mapSelectedRecords(state.records, keysAfterSelectAll);
                return {
                    ...state,
                    // Keep all keys that are not found in keys to deselect
                    selectedKeys: keysAfterSelectAll,
                    records,
                };
            }
            case 'DESELECT_ALL_KEYS': {
                // Side-effect: update records `isSelected` property
                // If there is nothing to deselect, return the same state
                if (state.selectedKeys.length === 0) {
                    return state;
                }
                let keysAfterDeselectAll = [];
                if (!action.clearAll) {
                    // Only deselect current records, but leave the other selection intact
                    // We'll do this by removing any records keys from the selected keys
                    const currentRecordKeys = (state.records || []).map((record) => record.key);
                    keysAfterDeselectAll = state.selectedKeys.filter((key) => !currentRecordKeys.includes(key));
                }
                // Perform a check to see if keys are equal regardless of order
                if (arraysEqual(state.selectedKeys, keysAfterDeselectAll)) {
                    // Keep the current state
                    return state;
                }
                const records = mapSelectedRecords(state.records, keysAfterDeselectAll);
                return {
                    ...state,
                    selectedKeys: keysAfterDeselectAll,
                    records,
                };
            }
            case 'SET_ERROR': {
                // Side-effect: stop loading
                return {
                    ...state,
                    isLoading: false,
                    hasError: action.error,
                };
            }
        }
    }
    return state;
}

const DEFAULT_SORT_ORDER = 'asc';
/**
 * The TableDataSource is a utility to keep your data and UI state in sync. It manages the following things:
 *
 * - pagination
 * - sorting
 * - filtering
 * - selection
 *
 * It works by keeping an internal state of parameters (such as page and page length) along
 * with your (filtered) data and emits events as soon as state changes happen.
 *
 * The input data can be one of three types:
 *
 * - A plain array of data
 *
 * Useful for a simple clientside set of data.
 *
 * - A promise that resolves with a plain array of data
 *
 * Useful for making an asynchronous call to a server that returns a simple set of data.
 *
 * - A function that returns a promise that resolves with an array of data
 *
 * Useful when you want each request for data to be sent to a server. The function receives the current
 * parameters (eg. page and page length) as its arguments, so you can use them in the API call. You keep
 * full control of the request parameters sent to your API and of transforming the results into
 * something that the TableDataSource can interpret.
 */
class TableDataSource {
    /**
     * Create a new TableDataSource object from an input that can be one of several things:
     *
     * - A plain array of data
     * - A promise that resolves with a plain array of data
     * - A function that returns a promise that resolves with an array of data
     *
     * @param input ((Function that returns a) promise that resolves with) an array of data
     * @param options Initial state and data (mapping) options
     */
    constructor(input, options) {
        /**
         * Single source of truth (immutable). Handles state and state change events.
         */
        this.store = new SimpleStore(tableDataSourceReducer);
        /**
         * An identifier for the last fired request.
         *
         * Prevents resolving slow requests that finish after another request has been made.
         */
        this.latestRequestIndex = 0;
        /**
         * Object used to map source data object to Record object.
         *
         * It is not kept in the store, and does not trigger data refresh when changed.
         */
        this.recordMapping = {};
        this._waitPromise = new Promise((resolve, reject) => {
            this._waitPromiseResolve = resolve;
            this._waitPromiseReject = reject;
        });
        this._waitTimeout = null;
        this._waitArgs = {};
        this.subscribe = (fn, preventEmitIfEqual = true) => {
            return this.store.subscribe(fn, preventEmitIfEqual);
        };
        if (Array.isArray(input) || input instanceof Promise) {
            // Create own request function for local data
            this.requestFunction = createRequestFunctionWithDataProvider(input);
        }
        else {
            this.requestFunction = input;
        }
        if (typeof options?.recordMapping === 'object' || typeof options?.recordMapping === 'function') {
            this.recordMapping = options.recordMapping;
        }
        if (typeof options?.retryLogic === 'function' || typeof options?.retryLogic === 'object') {
            this.retry = options.retryLogic;
        }
    }
    /**
     * Request new data (with current params).
     */
    async _refresh(newParams, retry = this.retry) {
        // Update internal request counter
        const requestIndex = ++this.latestRequestIndex;
        let retryCount = 0;
        let retryFunction;
        if (typeof retry === 'function') {
            // Manual function passed
            retryFunction = retry;
        }
        else if (typeof retry === 'object' && 'type' in retry) {
            // Get function by type
            retryFunction = getRetryFunction(retry);
        }
        const requestLoop = async (params) => {
            // Call the request function and wait for its result
            const res = await this.requestFunction(params, this);
            // If this instance has custom retry logic, get its result(ing params)
            const retryParams = typeof retryFunction === 'function' ? retryFunction(params, res, ++retryCount) : undefined;
            if (retryParams) {
                // Recurse but with new params
                return requestLoop(retryParams);
            }
            // If there's no retry logic or if it passed, return the result and the successful params
            return { res, successfulParams: params };
        };
        // Wrap request loop in try/catch to deal with errors
        let requestResult;
        let caughtError;
        try {
            // Start request loop beginning with new params
            requestResult = await requestLoop(this._getRequestParams(newParams));
        }
        catch (err) {
            caughtError = err instanceof Error ? err : new Error(String(err));
        }
        if (requestResult?.successfulParams) {
            // Store successful params
            this.store.dispatch({
                type: 'UPDATE_PARAMS',
                data: requestResult.successfulParams,
            });
        }
        // If we are not able to cancel an ongoing request/promise, simply ignore callbacks
        // from old requests that took longer to resolve than the latest request.
        if (requestIndex < this.latestRequestIndex) {
            return;
        }
        // Go into error state
        if (caughtError || !requestResult) {
            console.error(caughtError);
            this.store.dispatch({
                type: 'SET_ERROR',
                error: caughtError || new Error('Unknown error'),
            });
            return;
        }
        const { res, successfulParams } = requestResult;
        // Convert received data to records for use in data source
        const records = dataToRecords(res.data, this.recordMapping, calculateOffset(successfulParams.page, successfulParams.pageLength));
        // Update store
        this.store.dispatch({
            type: 'UPDATE',
            data: { records, total: res.total, totalUnfiltered: res.totalUnfiltered, ...successfulParams },
        });
        return res;
    }
    //
    // Public methods
    // ----------------------------
    refresh(newParams, retry = this.retry) {
        this.store.dispatch({
            type: 'SET_LOADING',
            isLoading: true,
        });
        // Merge these args with previous requests' arguments
        this._waitArgs = {
            newParams: { ...this._waitArgs.newParams, ...newParams },
            retry,
        };
        if (this._waitTimeout) {
            // Cancel previous request
            clearTimeout(this._waitTimeout);
            this._waitTimeout = null;
        }
        this._waitTimeout = setTimeout(async () => {
            try {
                const res = await this._refresh(this._waitArgs.newParams, this._waitArgs.retry);
                this._waitPromiseResolve?.(res);
            }
            catch (err) {
                this._waitPromiseReject?.(err);
            }
            finally {
                this._waitTimeout = null;
                this._waitArgs = {};
                this._waitPromise = new Promise((resolve, reject) => {
                    this._waitPromiseResolve = resolve;
                    this._waitPromiseReject = reject;
                });
            }
        }, 10);
        return this._waitPromise;
    }
    getState() {
        return this.store.getState();
    }
    isEmpty() {
        const { records } = this.store.getState();
        return Array.isArray(records) && records.length === 0;
    }
    //
    // Records
    // ----------------------------
    /**
     * Get all records in the store.
     *
     * If the records are currently loading, it will wait for loading to end before returning the result.
     */
    async waitForRecords() {
        return new Promise((resolve) => {
            // If currently loading
            if (this.getState().isLoading) {
                // Subscribe to change in loading state
                const unsub = this.store.subscribe((_, state) => {
                    // If no longer loading
                    if (state && state.isLoading === false) {
                        // unsubscribe
                        unsub();
                        // Resolve promise with records
                        resolve(state.records);
                    }
                }, ['isLoading']);
            }
            else {
                resolve(this.getState().records);
            }
        });
    }
    /**
     * Find record by unique key.
     *
     * Returns `undefined` if store is not populated or record not found.
     */
    findRecord(key) {
        const { records } = this.getState();
        return Array.isArray(records) ? records.find((record) => record.key === key) : undefined;
    }
    /**
     * Update record at unique key with new data.
     *
     * Returns updated record, or undefined is record is unknown.
     *
     * @param key Unique key of record
     * @param data New data
     */
    updateRecord(key, data) {
        const index = this.getRecordIndexByKey(key);
        if (typeof index === 'undefined') {
            return undefined;
        }
        const record = dataToRecord(data, this.recordMapping, key);
        // Update store
        this.store.dispatch({
            type: 'UPDATE_RECORD',
            record,
            index,
        });
        return record;
    }
    getRecordKeyByIndex(index) {
        const { records } = this.getState();
        const record = Array.isArray(records) ? records[index] : undefined;
        if (!record) {
            return;
        }
        return record.key;
    }
    getRecordIndexByKey(key) {
        const { records } = this.getState();
        const index = Array.isArray(records) ? records.findIndex((record) => record.key === key) : -1;
        if (index === -1) {
            return;
        }
        return index;
    }
    getRecords() {
        return this.getState().records;
    }
    //
    // Pagination
    // ----------------------------
    /**
     * Set page length and request new data.
     *
     * @param pageLength Set to `undefined` to disable page length limit
     */
    setPageLength(pageLength) {
        // Request data with new page length, and reset to page 1
        return this.refresh({ pageLength, page: 1 });
    }
    /**
     * Set page and request new data.
     */
    setPage(page) {
        return new Promise((resolve, reject) => {
            if (page === this.getState().page) {
                // Nothing to do here
                return resolve(page);
            }
            const pageValid = this._isPageValid(page);
            if (!pageValid) {
                return reject('Invalid page');
            }
            // Request new data with page
            this.refresh({ page }).then(() => {
                resolve(page);
            });
        });
    }
    /**
     * Offset current page by (negative) amount and request new data.
     */
    offsetPage(offset) {
        return this.setPage(this.getState().page + offset);
    }
    /**
     * Get the possible amount of pages calculated from the total records and page length.
     */
    getPageCount() {
        const { pageLength } = this.getState();
        if (typeof pageLength === 'undefined' || !pageLength) {
            // We can only have 1 page is there is no page length limit
            return 1;
        }
        // Even with 0 results, we'll always have at least one page
        return Math.ceil(this.getState().total / pageLength) || 1;
    }
    //
    // Sorting
    // ----------------------------
    /**
     * Set sort parameters and request new data.
     *
     * @param sortField (column) Defaults to current sort field
     * @param sortOrder Defaults to current sort order
     * @param sortType Only used for client-side sorting
     */
    sort(sortField = this.getState().sortField, sortOrder = this.getState().sortOrder, sortType) {
        let forceSortOrder = sortOrder;
        if (sortField && !sortOrder) {
            forceSortOrder = DEFAULT_SORT_ORDER;
        }
        // Do not re-request data if sorting parameters remain the same
        if (sortField === this.getState().sortField && sortOrder === this.getState().sortOrder) {
            return Promise.resolve(undefined);
        }
        return this.refresh({
            sortField,
            sortOrder: forceSortOrder,
            sortType,
        });
    }
    /**
     * Reverse current sort order and request new data.
     */
    reverseSort() {
        return this.refresh({
            sortOrder: this.getState().sortOrder === 'asc' ? 'dsc' : 'asc',
        });
    }
    //
    // Filtering
    // ----------------------------
    /**
     * Set filter and request new data.
     */
    filter(dataFilter) {
        return this.refresh({ dataFilter, page: 1 });
    }
    //
    // Selection
    // ----------------------------
    /**
     * Select records by their keys.
     *
     * @param keys Array of unique Record keys
     * @param clearAll Whether to clear the current selection (making `keys` the new selection)
     */
    select(keys, clearAll = false) {
        if (!Array.isArray(keys)) {
            return;
        }
        // Filter undefined values from given keys
        const _keys = keys.filter((k) => typeof k !== 'undefined');
        if (!_keys.length && !clearAll) {
            // Let's not bother the store if there's nothing to do
            return;
        }
        this.store.dispatch({
            type: 'SELECT_KEYS',
            keys: _keys,
            clearAll,
        });
    }
    /**
     * Deselect records by their keys.
     *
     * @param keys Array of unique Record keys
     */
    deselect(keys) {
        if (!Array.isArray(keys)) {
            return;
        }
        // Filter undefined values from given keys
        const _keys = keys.filter((k) => typeof k !== 'undefined');
        if (!_keys.length) {
            // We won't bother the store if there's nothing to do
            return;
        }
        this.store.dispatch({
            type: 'DESELECT_KEYS',
            keys: _keys,
        });
    }
    /**
     * Select all records in current page.
     *
     * Optionally clear the _entire_ existing selection before selecting new records by passing `true`.
     */
    selectPage(clearAll = false) {
        this.store.dispatch({
            type: 'SELECT_ALL_KEYS',
            clearAll,
        });
    }
    /**
     * Deselect all records in current page.
     *
     * Optionally clear the _entire_ selection by passing `true`.
     */
    deselectPage(clearAll = false) {
        this.store.dispatch({
            type: 'DESELECT_ALL_KEYS',
            clearAll,
        });
    }
    /**
     * Get all selected records.
     *
     * Warning: this is NOT always identical to the full selection. There may be selected keys that
     * are not present in the current record set, so the number of selected records can be lower
     * than the number of selected keys.
     */
    getSelectedRecords() {
        const { records } = this.getState();
        return records ? records.filter((record) => record.isSelected) : undefined;
    }
    /**
     * Gets all records that are selectable. If a record does not have an own setting
     * of whether or not its selectable, it will default to the selectableDefault.
     *
     * @param selectableDefault The default selectable configuration
     */
    getSelectableRecords(selectableDefault) {
        const { records } = this.getState();
        return records
            ? records.filter((record) => (typeof record.selectable === 'boolean' ? record.selectable : selectableDefault))
            : undefined;
    }
    /**
     * Get offset number for page and pageLength.
     *
     * @example
     * calculateOffset(1, 10); // 0
     * calculateOffset(3, 10); // 20
     */
    calculateOffset(page, pageLength) {
        return calculateOffset(page, pageLength);
    }
    //
    // Internal
    // ----------------------------
    _getRequestParams(newParams) {
        const { page, pageLength, sortField, sortOrder, sortType, dataFilter } = this.getState();
        let forceSortOrder = sortOrder;
        if (sortField && !sortOrder) {
            forceSortOrder = DEFAULT_SORT_ORDER;
        }
        return {
            page,
            pageLength,
            sortField,
            sortOrder: forceSortOrder,
            sortType,
            dataFilter,
            ...newParams,
        };
    }
    _isPageValid(page) {
        if (page < 1 || page > this.getPageCount()) {
            return false;
        }
        return true;
    }
}

const ERROR_TITLE = 'Failure to retrieve data';
const ERROR_DESCRIPTION = "We're currently unable to retrieve data, please check back at another time or contact support.";
const EMPTY_TITLE = 'No data retrieved';
const EMPTY_DESCRIPTION = 'No data is available for this query. If applicable, update your filter settings or please check again later.';

const tableCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;display:block;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;min-width:100%;position:relative;width:0}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.container{background-color:var(--jw-theme-surface-tile-bg-color);position:relative}.state-container{display:none;height:100%;top:0;width:100%}.state,.state-container{bottom:0;left:0;pointer-events:none;position:absolute;right:0}.state{align-items:center;display:flex;justify-content:center;opacity:0;text-align:center;top:42px;transition:opacity .3s ease;z-index:1}.state p{max-width:50ch;white-space:pre-line}.state-title{font-size:15px!important}.state-description{font-size:13px!important;margin:0!important}:host([selectable]) wui-skeleton-line{left:32px}:host([error-type=error]) wui-table-row,:host([is-empty]) wui-table-row{opacity:.1;pointer-events:none}:host([error-type=error]) .state-container,:host([is-empty]) .state-container{display:block}:host([error-type=error]) wui-table-row:not(:nth-child(n+1):nth-child(-n+11)),:host([is-empty]) wui-table-row:not(:nth-child(n+1):nth-child(-n+11)){display:none}:host([error-type=error]) .state.error{opacity:1;pointer-events:all}:host([is-empty]:not([error-type=error])) wui-table-header-row{opacity:.1}:host([is-empty]:not([error-type=error])) .state.empty{opacity:1;pointer-events:all}:host([loading]) .cell{opacity:0}";

const WuiTable$1 = /*@__PURE__*/ proxyCustomElement(class WuiTable extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.sortChanged = createEvent(this, "sortChanged", 3);
        this.pageChanged = createEvent(this, "pageChanged", 3);
        this.pageLengthChanged = createEvent(this, "pageLengthChanged", 3);
        this.selectedKeysChanged = createEvent(this, "selectedKeysChanged", 3);
        this.tableDataSourceChanged = createEvent(this, "tableDataSourceChanged", 3);
        //
        // Columns
        // ----------------------------
        /**
         * Column definitions
         */
        this.columns = [];
        //
        // Sorting
        // ----------------------------
        /**
         * If table can be sorted by column
         */
        this.sortable = false;
        //
        // Pagination
        // ----------------------------
        /**
         * Current page
         */
        this.page = 1;
        /**
         * Current page length
         */
        this.pageLength = TablePageLength.s;
        /**
         * Pagination mode
         *
         * - auto (default): will provide pagination when necessary
         * - on: will always render paginator
         * - off: will render all rows without limitation and hide paginator
         */
        this.paginationMode = 'auto';
        /**
         * Available page length options
         */
        this.pageLengths = [TablePageLength.s, TablePageLength.m, TablePageLength.l];
        /**
         * Keys of selected rows
         */
        this.selectedKeys = [];
        this.selectionStatus = 'none';
        //
        // Styling
        // ----------------------------
        /**
         * Table row density/height.
         */
        this.density = 'md';
        /**
         * Title for error state
         */
        this.errorTitle = ERROR_TITLE;
        /**
         * Description for error state
         */
        this.errorDescription = ERROR_DESCRIPTION;
        /**
         * Title for empty state
         */
        this.emptyTitle = EMPTY_TITLE;
        /**
         * Description for empty state
         */
        this.emptyDescription = EMPTY_DESCRIPTION;
        /**
         * Debounces the loading UI based on the store's loading state.
         */
        this.loadingDebouncer = createStateDebouncer(150, 
        // Add 150ms for time it takes to render/animate
        250 + 550);
        //
        // Columns
        // ----------------------------
        this.getVisibleColumns = (visibleColumnNames) => {
            return this.columns?.filter((col) => typeof visibleColumnNames === 'undefined' ? true : visibleColumnNames.includes(col.name));
        };
        //
        // Sorting
        // ----------------------------
        this.handleSort = (event) => {
            if (!this.tableDataSource) {
                return;
            }
            const newSortField = event.detail;
            let { sortField, sortOrder } = this.tableDataSource.getState();
            // If this is already the sorted column
            if (newSortField === sortField) {
                // Flip the order
                sortOrder = sortOrder === 'asc' ? 'dsc' : 'asc';
            }
            else {
                // Sort this column
                sortField = newSortField;
                // Set default sort order
                if (sortOrder !== DEFAULT_SORT_ORDER) {
                    sortOrder = DEFAULT_SORT_ORDER;
                }
            }
            this.tableDataSource.sort(sortField, sortOrder);
        };
        //
        // Pagination
        // ----------------------------
        this.handleChangePageLength = (event) => {
            const newPageLength = event.detail;
            this.tableDataSource?.setPageLength(newPageLength);
        };
        this.handleChangePage = (event) => {
            const newPage = event.detail;
            this.tableDataSource?.setPage(newPage);
        };
        this.onSelectRecord = (e) => this.handleSelectRow(e.detail);
    }
    // Re-init TableDataSource when dataSource changes
    _dataSource(newValue) {
        newValue && this.initTableDataSource(newValue);
    }
    // Manually update TableDataSource if possible
    _recordMapping(newValue) {
        if (newValue && this.tableDataSource) {
            this.tableDataSource.recordMapping = newValue;
        }
    }
    // Manually update TableDataSource if possible
    _retryLogic(newValue) {
        if (newValue && this.tableDataSource) {
            this.tableDataSource.retry = newValue;
        }
    }
    // Propagate to TableDataSource
    _sortField(newValue) {
        newValue && this.tableDataSource?.sort(newValue);
    }
    // Propagate to TableDataSource
    _sortOrder(newValue) {
        newValue && this.tableDataSource?.sort(this.sortField, newValue);
    }
    // Propagate to TableDataSource
    _page(newValue) {
        newValue && this.tableDataSource?.setPage(newValue);
    }
    // Propagate to TableDataSource
    _pageLength(newValue) {
        this.tableDataSource?.setPageLength(newValue);
    }
    _paginationMode(newValue = 'auto') {
        if (newValue === 'off') {
            // Disable pageLength in data source when pagination is turned off
            this.tableDataSource?.setPageLength(undefined);
        }
    }
    // Propagate to TableDataSource
    _dataFilter(newValue) {
        this.tableDataSource?.filter(newValue);
    }
    // Propagate to TableDataSource
    _selectedKeys(newValue) {
        // Override selection
        this.tableDataSource?.select(newValue || [], true);
    }
    async getTableDataSource() {
        return this.tableDataSource;
    }
    componentWillLoad() {
        this.dataSource && this.initTableDataSource(this.dataSource);
    }
    //
    // DataSource
    // ----------------------------
    async initTableDataSource(dataSource = this.dataSource, options = { recordMapping: this.recordMapping, retryLogic: this.retryLogic }) {
        if (!dataSource) {
            return;
        }
        // Clear loading state
        this.loading = undefined;
        this.tableDataSource = new TableDataSource(dataSource, options);
        this.tableDataSource.subscribe((prevState, state) => {
            // Wait for loading to finish before update records
            const debounceUpdateRecords = state?.isLoading !== prevState?.isLoading;
            // Only update local state if the records are new
            const updateRecords = prevState?.records !== state?.records;
            this.loadingDebouncer(!!state?.isLoading, (isLoading) => {
                requestAnimationFrame(() => {
                    // Persist debounced loading value to local state
                    this.loading = isLoading;
                    // Update records along with loading status
                    if (debounceUpdateRecords && updateRecords) {
                        this.records = state?.records;
                    }
                });
            });
            // Update records right away (don't wait for loading status)
            if (!debounceUpdateRecords && updateRecords) {
                this.records = state?.records;
            }
            // Update selection status when selected keys or records have changed
            if (prevState?.selectedKeys !== state?.selectedKeys || prevState?.records !== state?.records) {
                this.determineSelectionStatus();
            }
            if (!state) {
                return;
            }
            if (prevState?.selectedKeys !== state.selectedKeys) {
                // Update own state
                this.selectedKeys = state.selectedKeys;
                this.selectedKeysChanged.emit(this.selectedKeys);
            }
            if (prevState?.sortField !== state.sortField || prevState?.sortOrder !== state.sortOrder) {
                let forceSortOrder = state.sortOrder;
                if (state.sortField && !state.sortOrder) {
                    forceSortOrder = DEFAULT_SORT_ORDER;
                }
                this.sortChanged.emit([state.sortField, forceSortOrder]);
            }
            if (prevState?.page !== state.page) {
                this.pageChanged.emit(state.page);
            }
            if (prevState?.pageLength !== state.pageLength) {
                this.pageLengthChanged.emit(state.pageLength);
            }
        });
        // Make first request for data with initial parameters
        this.tableDataSource.refresh({
            page: this.page,
            pageLength: this.paginationMode === 'off' ? undefined : this.pageLength,
            sortField: this.sortField,
            sortOrder: this.sortOrder,
            dataFilter: this.dataFilter,
        });
        this.tableDataSourceChanged.emit(this.tableDataSource);
    }
    hasError() {
        return this.tableDataSource ? this.tableDataSource.getState().hasError : undefined;
    }
    isEmpty() {
        if (!this.dataSource) {
            return true;
        }
        return this.tableDataSource ? this.tableDataSource.isEmpty() : false;
    }
    //
    // Selection
    // ----------------------------
    handleSelectAllRows(select) {
        if (this.disabled || !this.tableDataSource) {
            return;
        }
        if (this.selectable === 'single') {
            return;
        }
        if (select) {
            // Toggle all on
            this.tableDataSource.selectPage(false);
        }
        else {
            // Toggle all off
            this.tableDataSource.deselectPage(false);
        }
    }
    handleSelectRow(key) {
        if (this.disabled || !this.tableDataSource) {
            return;
        }
        const selectedRecords = this.tableDataSource.getSelectedRecords() || [];
        // If already selected
        if (selectedRecords.find((r) => r.key === key)) {
            this.tableDataSource.deselect([key]);
        }
        else {
            this.tableDataSource.select([key], this.selectable === 'single');
        }
    }
    determineSelectionStatus() {
        if (!this.tableDataSource) {
            return;
        }
        const { selectedKeys } = this.tableDataSource.getState();
        // Selection is always none if there are zero selected keys
        if (selectedKeys.length === 0) {
            this.selectionStatus = 'none';
            return;
        }
        const selectableRecords = this.tableDataSource.getSelectableRecords(!!this.selectable);
        const selectedRecords = this.tableDataSource.getSelectedRecords();
        if (!(selectableRecords && selectedRecords)) {
            return;
        }
        // First check if all rows on this page are selected. This takes precedence.
        if (selectedRecords.length > 0 && selectedRecords.length === selectableRecords.length) {
            this.selectionStatus = 'all';
        }
        else if (selectedKeys.length) {
            // If there's a selected key, we at least have SOME selection _somewhere_
            this.selectionStatus = 'some';
        }
    }
    render() {
        const visibleColumns = this.getVisibleColumns(this.visibleColumnNames);
        const placeholders = Array.from(Array(typeof this.placeholderPageLength !== 'undefined' ? this.placeholderPageLength : this.pageLength));
        const rowsSelected = Boolean(this.tableDataSource?.getState().selectedKeys.length);
        const isSelectable = typeof this.selectable !== 'undefined';
        const isEmpty = this.isEmpty();
        const hasError = this.hasError();
        return (h(Host, { key: 'aec8349e20cd44d8668fffafae2127e0d7e1b39a', loading: this.loading, "error-type": hasError ? 'error' : undefined, "is-empty": isEmpty, "rows-selected": rowsSelected, disabled: this.disabled }, h("div", { key: 'fd46c6e453867f8ad73f1164f8ac7c07731e46e2', class: "container" }, h("wui-table-header-row", { key: 'd729777d82a4e633af9c5067e5c767d307733d34', visibleColumns: visibleColumns, selectable: this.selectable, sortable: this.sortable, sortField: this.tableDataSource?.getState().sortField, sortOrder: this.tableDataSource?.getState().sortOrder, selectionStatus: this.selectionStatus, selectedKeysLength: this.tableDataSource?.getState().selectedKeys.length, disabled: this.loading || this.disabled || isEmpty, onSort: this.handleSort, onSelectAllRows: (e) => this.handleSelectAllRows(e.detail), selectionActionButtons: this.selectionActionButtons, tableDataSource: this.tableDataSource, rowsSelected: rowsSelected, rowMenuItems: Boolean(this.rowMenuItems?.length), onDisableAll: (evt) => {
                this.disabled = evt.detail;
            } }), (this.records?.length ? this.records : placeholders).map((record, i) => {
            return (h("wui-table-row", { key: i, idx: i, selectable: isSelectable, record: record ? { ...record } : undefined, visibleColumns: visibleColumns, columnTemplates: this.columnTemplates, hoverActionsTemplate: this.hoverActionsTemplate, hoverActions: this.hoverActions, loading: this.loading, disabled: this.loading || this.disabled, onSelectRecord: this.onSelectRecord, rowsSelected: rowsSelected, density: this.density, rowMenuItems: this.rowMenuItems }));
        }), h("div", { key: '385340552b534c8b6bbed3aa66fbc45a224e91a7', class: "state-container" }, h("div", { key: '95927d9a526d68832a8218a29dfbd9ed92fc2a82', class: "state error" }, h("slot", { key: '32d967932d495fdc90ff0df551685d2e89ef006b', name: "state-error" }), !slotFilled(this.elm, 'state-error') && (h("div", { key: 'ee5c5450ad0aeadde0767a9119f2b928d1786533' }, h("wui-icon", { key: '9eae0ab7f51fe35c7f45df1392b5cbe7a81948fb', name: "alert-circle", size: "xl" }), this.errorTitle && h("h3", { key: '544c71248b5647d56a9a907e9bf99d75a3272dfb', class: "state-title" }, this.errorTitle), this.errorDescription && h("p", { key: '134097eccee58f9b6df0e895c28067b393e53430', class: "state-description" }, this.errorDescription)))), h("div", { key: '2403ba4842f5c3152981e57d701b68a2921091ff', class: "state empty" }, h("slot", { key: '6be670777f87f5ab9ad73311a9afeda7d482b1d0', name: "state-empty" }), !slotFilled(this.elm, 'state-empty') && (h("div", { key: 'f8e180eb6aefc55337a1ad37b7a21c867ec08be2' }, h("svg", { key: '09b1498453b031e444fbdeb896bcbee74ac07d42', width: "144", height: "144", viewBox: "0 0 144 144" }, h("title", { key: '8f388918cf4d088ee7e388b5a23c56ad26e15722' }, "Empty"), h("defs", { key: 'dc618e3184debc94eab0674dbba7f4dc51e40003' }, h("filter", { key: '1b06af2036fd47de18a12fe8eaaa85a497b7334e', id: "prefix__a", width: "122.3%", height: "156.8%", x: "-11.2%", y: "-28.4%", filterUnits: "objectBoundingBox" }, h("feOffset", { key: 'c8043351b7a9ffde9db99b10ffc2fd7d70d96b0b', in: "SourceAlpha", result: "shadowOffsetOuter1" }), h("feGaussianBlur", { key: '3e7ef96287dac148f84f7e073a849e3673813c69', in: "shadowOffsetOuter1", result: "shadowBlurOuter1", stdDeviation: "3.5" }), h("feColorMatrix", { key: 'c8ee6c304b7e938e4434aaf84e8526c137bf70f3', in: "shadowBlurOuter1", values: "0 0 0 0 0.847058824 0 0 0 0 0.866666667 0 0 0 0 0.901960784 0 0 0 0.3 0" })), h("rect", { key: '23318f473a3e5b690a3f8a06da6fe5ba3edc5d8a', id: "prefix__b", width: "94", height: "37", x: "0", y: ".5", rx: "1.8" })), h("g", { key: '0a343726b65857c4e0aaf5a396513fcb4164731a', fill: "none", "fill-rule": "evenodd" }, h("circle", { key: '93e8159fec4973745c83b0136bd3f07f932de89f', cx: "72", cy: "72", r: "72", fill: "#D8DDE6", "fill-opacity": ".3" }), h("g", { key: '2c987e7c9ae6ce23012c9f6b08d14c587d990195', transform: "translate(25 53)" }, h("use", { key: '29417187e78e1f213a4c77c0ae99b625bdd8a328', fill: "#000", filter: "url(#prefix__a)", xlinkHref: "#prefix__b" }), h("use", { key: '4e8465d1bc97b649e64f3f7d113a9249616c7ac5', fill: "#FFF", xlinkHref: "#prefix__b" }), h("rect", { key: '01e00b09173ba2df05a0449cdc9bee41a9b36425', width: "58", height: "4", x: "28", y: "9.5", fill: "#D8DDE6", rx: "2" }), h("rect", { key: '9dc0d4dfd4aa9ff8bce4448f89928509ea1da652', width: "14", height: "4", x: "8", y: "9.5", fill: "#D8DDE6", rx: "2" }), h("rect", { key: '5176ebd29a0ec9d3991456657632272152931fba', width: "46", height: "4", x: "28", y: "20.5", fill: "#D8DDE6", rx: "2" }), h("rect", { key: 'e64ec2728401e3d08afa2ae8a7c48b7c98a07717', width: "14", height: "4", x: "8", y: "20.5", fill: "#D8DDE6", rx: "2" })))), this.emptyTitle && h("h3", { key: '46280f8c7e05041c3fca1cd7d625dd268ca2462f', class: "state-title" }, this.emptyTitle), this.emptyDescription && h("p", { key: 'f7d67d14cd3f3be5afbb82d227913b64959bae02', class: "state-description" }, this.emptyDescription))))), h("slot", { key: '8e127ebbd84e86822075687681e5917c5e6180b8' })), h("wui-pagination", { key: 'f0816f963557a9b6c0bf35baf5e45731534439bd', loading: this.loading, disabled: this.disabled, hasError: Boolean(hasError), total: this.tableDataSource?.getState().total, page: this.tableDataSource?.getState().page, pageLength: this.tableDataSource?.getState().pageLength, pageLengths: this.pageLengths, onChangePageLength: this.handleChangePageLength, onChangePage: this.handleChangePage, paginationMode: this.paginationMode, itemsOnPage: this.tableDataSource?.getState().records?.length })));
    }
    get elm() { return this; }
    static get watchers() { return {
        "dataSource": ["_dataSource"],
        "recordMapping": ["_recordMapping"],
        "retryLogic": ["_retryLogic"],
        "sortField": ["_sortField"],
        "sortOrder": ["_sortOrder"],
        "page": ["_page"],
        "pageLength": ["_pageLength"],
        "paginationMode": ["_paginationMode"],
        "dataFilter": ["_dataFilter"],
        "selectedKeys": ["_selectedKeys"]
    }; }
    static get style() { return tableCss; }
}, [257, "wui-table", {
        "dataSource": [16, "data-source"],
        "recordMapping": [16, "record-mapping"],
        "retryLogic": [16, "retry-logic"],
        "columns": [16],
        "visibleColumnNames": [16, "visible-column-names"],
        "columnTemplates": [16, "column-templates"],
        "hoverActionsTemplate": [16, "hover-actions-template"],
        "hoverActions": [16, "hover-actions"],
        "rowMenuItems": [16, "row-menu-items"],
        "sortable": [4],
        "sortField": [1, "sort-field"],
        "sortOrder": [1, "sort-order"],
        "page": [2],
        "pageLength": [2, "page-length"],
        "placeholderPageLength": [2, "placeholder-page-length"],
        "paginationMode": [1, "pagination-mode"],
        "pageLengths": [16, "page-lengths"],
        "dataFilter": [16, "data-filter"],
        "selectable": [513],
        "selectionActionButtons": [16, "selection-action-buttons"],
        "selectedKeys": [1040, "selected-keys"],
        "density": [513],
        "disabled": [4],
        "errorTitle": [1, "error-title"],
        "errorDescription": [1, "error-description"],
        "emptyTitle": [1, "empty-title"],
        "emptyDescription": [1, "empty-description"],
        "selectionStatus": [32],
        "loading": [32],
        "records": [32],
        "getTableDataSource": [64]
    }, undefined, {
        "dataSource": ["_dataSource"],
        "recordMapping": ["_recordMapping"],
        "retryLogic": ["_retryLogic"],
        "sortField": ["_sortField"],
        "sortOrder": ["_sortOrder"],
        "page": ["_page"],
        "pageLength": ["_pageLength"],
        "paginationMode": ["_paginationMode"],
        "dataFilter": ["_dataFilter"],
        "selectedKeys": ["_selectedKeys"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-table", "wui-box", "wui-button", "wui-check-input", "wui-divider", "wui-form-field", "wui-icon", "wui-layout", "wui-list", "wui-list-item", "wui-list-item-divider", "wui-list-item-header", "wui-loader", "wui-menu-pane", "wui-more-menu", "wui-pagination", "wui-skeleton-line", "wui-status", "wui-table-header-row", "wui-table-page-length-input", "wui-table-page-number-input", "wui-table-row", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-table":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiTable$1);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$o();
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$n();
            }
            break;
        case "wui-check-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$m();
            }
            break;
        case "wui-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$l();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$k();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$j();
            }
            break;
        case "wui-layout":
            if (!customElements.get(tagName)) {
                defineCustomElement$i();
            }
            break;
        case "wui-list":
            if (!customElements.get(tagName)) {
                defineCustomElement$h();
            }
            break;
        case "wui-list-item":
            if (!customElements.get(tagName)) {
                defineCustomElement$g();
            }
            break;
        case "wui-list-item-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$f();
            }
            break;
        case "wui-list-item-header":
            if (!customElements.get(tagName)) {
                defineCustomElement$e();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$d();
            }
            break;
        case "wui-menu-pane":
            if (!customElements.get(tagName)) {
                defineCustomElement$c();
            }
            break;
        case "wui-more-menu":
            if (!customElements.get(tagName)) {
                defineCustomElement$b();
            }
            break;
        case "wui-pagination":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "wui-skeleton-line":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-table-header-row":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-table-page-length-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-table-page-number-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-table-row":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}
defineCustomElement$1();

const WuiTable = WuiTable$1;
const defineCustomElement = defineCustomElement$1;

export { WuiTable, defineCustomElement };
